'use client';
import { useEffect } from 'react';

import { useUserStore } from '@utils/user';

interface Props {
  message: string;
}

// Function to check if an element is visible
const isVisible = (element) => {
  document.body.appendChild(element);
  const computedStyle = window.getComputedStyle(element);
  const isVisible =
    computedStyle.display !== 'none' &&
    computedStyle.visibility !== 'hidden';
  document.body.removeChild(element);

  return isVisible;
};

// Recursive function to filter out invisible elements
const filterVisibleNodes = (node) => {
  if (node.nodeType === Node.ELEMENT_NODE) {
    if (isVisible(node)) {
      const clonedNode = node.cloneNode(false);
      Array.from(node.childNodes).forEach((childNode) => {
        const filteredChild = filterVisibleNodes(childNode);
        if (filteredChild) {
          clonedNode.appendChild(filteredChild);
        }
      });
      return clonedNode;
    } else {
      return null;
    }
  } else {
    return node.cloneNode(true);
  }
};

export default function CopyListener({
  message,
}: Props) {
  const userStore = useUserStore();
  useEffect(() => {
    const copyListener = (event) => {
      if (!userStore.user?.email && userStore.user?.email.indexOf('@trans.info') !== -1) {
        return;
      }
      const range = window.getSelection()?.getRangeAt(0) || new Range(),
        rangeContents = range.cloneContents(),
        pageLink = `${message}${document.location.href}`,
        helper = document.createElement('div');

      // Filter out invisible elements from rangeContents
      Array.from(rangeContents.childNodes).forEach((node) => {
        const filteredNode = filterVisibleNodes(node);
        if (filteredNode) {
          helper.appendChild(filteredNode);
        }
      });

      event.clipboardData.setData(
        'text/plain',
        `${helper.innerText}\n${pageLink}`,
      );
      event.clipboardData.setData(
        'text/html',
        `${helper.innerHTML}<br>${pageLink}`,
      );
      event.preventDefault();
    };
    document.addEventListener('copy', copyListener);
    return () => {
      document.removeEventListener('copy', copyListener);
    }
  }, [userStore.user?.email]);

  return <></>;
}
